import { useEffect } from 'react'

import { useHotRequest } from '@hotmart-org-ca/hot-club-http/client/hooks'
import { useThemeContext } from '@space-core/hooks'
import useTranslation from '@space-core/hooks/useTranslation'
import BaseLayout from '@space-shell/common/components/baseLayout'
import LoaderFull from '@space-shell/common/components/loaderFull'
import { AuthStatus, useAuthContext } from '@space-shell/common/contexts/auth'
import { useGoogleAnalyticsPageView } from '@space-shell/common/hooks/useGoogleAnalyticsPageView'
import { AuthService } from '@space-shell/common/services/auth'
import { MembershipEndpoints } from '@space-shell/common/services/membership/constants'
import type { Membership } from '@space-shell/common/services/membership/types'
import { UserEndpoints } from '@space-shell/common/services/user'
import { CookieUtils } from '@space-shell/common/utils/cookie'
import { LanguageUtils } from '@space-shell/common/utils/language'
import { APP_SPACE_HOT_HTTP_INSTANCES } from '@space-shell/config/request/providers/hotClubHttp/constants'
import { IS_CUSTOM_DOMAIN } from '@space-shell/constant/domain'
import {
  useAppStore,
  useAuthStore,
  useMembershipStore,
  useUserInfoStore
} from '@space-shell/core/store'
import type { UserInfoType } from '@space-shell/core/store'
import { lazyRemote } from '@space-shell/lib/runtime'
import { useParams, Outlet, useNavigate, useLocation, useSearchParams } from 'react-router-dom'

const UserProfileProvider = lazyRemote('community/userProfileContext', 'UserProfileProvider')
const LANG_COOKIE = 'hmLangCookie'

type RouteParams = {
  membership: string
  lang: string
}

/**
 * @component Deprecated Component
 *
 * @description
 * This component is deprecated and used only for standalone purpose.
 * It is just to handle authentication locally when running in standalone mode. This component is not used in production environments.
 *
 * @deprecated since version 1.93.0 - Use {@link remotes/clubServerSide/router/routeGuard.tsx} instead.
 *
 */
const RouteGuard = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { token, ucode, id: userId } = useAuthStore()
  const { membership, lang } = useParams<RouteParams>()
  const { authStatus, setAuthStatus } = useAuthContext()
  const { i18n } = useTranslation()
  const { theme } = useThemeContext()
  const [searchParams] = useSearchParams()
  const { setFirstAccessComplete, firstAccessComplete } = useAppStore()

  const isAuthenticating = authStatus === AuthStatus.AUTHENTICATING
  const isAuthenticated = authStatus === AuthStatus.AUTHENTICATED
  const isRenewing = authStatus === AuthStatus.RENEWING

  const membershipSlug = String(membership)

  /**
   * Note: This request is necessary to invalidate membership data when navigating between courses
   * from different slugs within the user's profile, as soon as possible remove this duplicate, as
   * this request is already being made on the server-side
   */
  const { data: membershipData } = useHotRequest(MembershipEndpoints.info(membershipSlug), {
    instanceName: APP_SPACE_HOT_HTTP_INSTANCES.API_CONTENT_GATEWAY,
    cacheKey: ['membershipInfo', membershipSlug],
    queryOptions: {
      enabled: Boolean(membershipSlug),
      onSuccess: (membershipData: Membership) => {
        useMembershipStore.setState({ membershipData, membershipSlug: membershipData.slug })
      }
    }
  })

  useHotRequest(`${UserEndpoints.getInfo({ slug: membershipSlug, userId })}`, {
    instanceName: APP_SPACE_HOT_HTTP_INSTANCES.API_CONTENT_GATEWAY,
    cacheKey: [UserEndpoints.getInfo({ slug: membershipSlug, userId })],
    params: {
      dimensions: '48x48'
    },
    queryOptions: {
      enabled: Boolean(membershipData) && isAuthenticated && Boolean(userId),
      onSuccess: (userInfo: UserInfoType) => {
        useUserInfoStore.setState({ ...userInfo })
      }
    }
  })

  useGoogleAnalyticsPageView()

  useEffect(() => {
    const langCookie = CookieUtils.getCookieByKey(document.cookie, LANG_COOKIE)

    if (!langCookie) {
      CookieUtils.setCookie(LANG_COOKIE, String(lang))
    }

    const normalizedLocale = LanguageUtils.formatLocale(langCookie ?? String(lang))
    const normalizedPath = location.pathname.replace(String(lang), normalizedLocale.toLowerCase())

    LanguageUtils.setLanguage(normalizedLocale, i18n)
    navigate({ pathname: normalizedPath, search: location.search }, { replace: true })
  }, [lang, i18n, location.pathname, location.search, navigate])

  useEffect(() => {
    if (IS_CUSTOM_DOMAIN) {
      if (firstAccessComplete) {
        return
      }

      const signInSilentCallbackParam = searchParams.get('signinSilentCallback')

      if (signInSilentCallbackParam === 'login_required') {
        setFirstAccessComplete(true)
        setAuthStatus?.(AuthStatus.UNAUTHENTICATED)
      }

      AuthService.start({ signInRedirect: false })
    }
  }, [searchParams, setFirstAccessComplete, firstAccessComplete, setAuthStatus])

  const shouldWaitForAuthentication = (IS_CUSTOM_DOMAIN && isAuthenticating) || isRenewing

  const shouldRenderLoading = shouldWaitForAuthentication

  if (shouldRenderLoading) {
    return <LoaderFull />
  }

  return (
    <>
      <UserProfileProvider token={token} loggedUserUcode={ucode} locale={lang} theme={theme} />
      <BaseLayout>
        <Outlet />
      </BaseLayout>
    </>
  )
}

export default RouteGuard
